import { FinancialTool } from "../types";

export const budgetTools: FinancialTool[] = [
  {
    id: "budget-planner",
    name: "Budget Planner",
    description: "Plan your household budget using the classic 50/30/20 monthly budgeting standard.",
    category: "budget",
    isHighCPC: true,
    inputs: [
      { id: "netIncome", label: "Monthly Take-Home Pay ($)", type: "currency", defaultValue: 5500, min: 500, max: 50000, step: 100 }
    ],
    calculate: (inputs) => {
      const income = inputs.netIncome || 0;

      const needs = income * 0.50;
      const wants = income * 0.30;
      const savings = income * 0.20;

      return {
        outputs: [
          { label: "Essential Needs (50% Bucket)", value: needs, type: "currency", highlighted: true },
          { label: "Discretionary Wants (30% Bucket)", value: wants, type: "currency" },
          { label: "Savings & Debt Reduction (20% Bucket)", value: savings, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Essential Needs (50%)", value: needs },
          { name: "Discretionary Wants (30%)", value: wants },
          { name: "Savings & Debt (20%)", value: savings }
        ]
      };
    },
    guide: {
      overview: "Plan your household budget using the classic 50/30/20 monthly budgeting standard.",
      howToUse: "Enter your overall net monthly take-home pay, and view your recommended budget distributions.",
      formulaExplanation: "Divisibilizes your income into three primary spending categories: Needs (50%), Wants (30%), and Savings (20%).",
      faq: [
        { question: "What represents an essential need?", answer: "Essential costs required for basic survival, including housing rent, utilities, groceries, health insurance, and debt minimums." },
        { question: "What represents a discretionary want?", answer: "Non-essential costs that upgrade your lifestyle, including dining out, travel, hobbies, stream services, and ticket seats." }
      ],
      seoContent800Words: `Building a stable household budget is a primary objective of personal finance, and the classic **50/30/20 Budgeting Rule** is one of your most valuable tools. Popularized by Senator Elizabeth Warren in her book *All Your Worth*, the 50/30/20 standard helps you organize your income and expenses, completely bypassing complex spreadsheets and tracking systems.

### Organizing Your Income into Three Primary Buckets

The 50/30/20 rule divides your after-tax net take-home pay into three primary spending buckets:
1. **Essential Needs (50%)**: Essential costs required for basic survival. These include housing costs, utilities, groceries, car payments, health insurance, and minimum required debt payments. If a cost is completely necessary for your life, it belongs in this bucket.
2. **Discretionary Wants (30%)**: Non-essential costs that upgrade your lifestyle. These include hobbies, travel, dining out, streaming services, and lifestyle shopping. If a cost is non-essential, it belongs in this bucket.
3. **Savings and Debt (20%)**: Savings, investments, and accelerated debt reduction. These include retirement contributions, emergency funds, brokerage investments, and accelerated prepayments on high-interest loans.

On a monthly take-home pay of $5,500:
* Your **Essential Needs (50%)** limit is:
  $$\\text{Needs} = \\$5,500 \\times 0.50 = \\$2,750$$
* Your **Discretionary Wants (30%)** limit is:
  $$\\text{Wants} = \\$5,500 \\times 0.30 = \\$1,650$$
* Your **Savings and Debt (20%)** target is:
  $$\\text{Savings} = \\$5,500 \\times 0.20 = \\$1,100$$

Our tool uses these calculations to help you draft your budget plan clearly and track your goals.

### Balancing Needs and Wants to Build Wealth

* **Review Your Needs Limit**: If your housing and living costs exceed 50% of your income, you are "house poor" or overextended, meaning a sudden drop in income can quickly put your finances in danger. To protect yourself, focus on lowering your needs by cutting bills or housing costs.
* **Keep Your Wants Flexible**: Because wants are non-essential, you can quickly cut them during economic corrections or emergencies, providing a safe cushion to protect your budget.

This tool helps you analyze your spending buckets clearly so you can optimize your cash flows.`
    }
  },
  {
    id: "monthly-expense-calculator",
    name: "Monthly Expense Tracker",
    description: "Track and organize your monthly living expenses by spending category.",
    category: "budget",
    inputs: [
      { id: "housing", label: "Housing Cost (Rent/Mortgage) ($)", type: "currency", defaultValue: 1800, min: 0, max: 10000, step: 50 },
      { id: "utilities", label: "Utilities (Utilities/Water/Mobile) ($)", type: "currency", defaultValue: 350, min: 0, max: 2000, step: 10 },
      { id: "groceries", label: "Groceries Spending ($)", type: "currency", defaultValue: 600, min: 0, max: 3000, step: 20 },
      { id: "transportation", label: "Transportation (Auto/Gas) ($)", type: "currency", defaultValue: 450, min: 0, max: 5000, step: 10 },
      { id: "personal", label: "Personal Shopping & Dining ($)", type: "currency", defaultValue: 700, min: 0, max: 10000, step: 20 }
    ],
    calculate: (inputs) => {
      const housing = inputs.housing || 0;
      const utilities = inputs.utilities || 0;
      const groceries = inputs.groceries || 0;
      const trans = inputs.transportation || 0;
      const personal = inputs.personal || 0;

      const total = housing + utilities + groceries + trans + personal;

      return {
        outputs: [
          { label: "Total Combined Monthly Expenses", value: total, type: "currency", highlighted: true },
          { label: "Housing Percentage (%)", value: total > 0 ? (housing / total) * 100 : 0, type: "percentage" },
          { label: "Groceries Percentage (%)", value: total > 0 ? (groceries / total) * 100 : 0, type: "percentage" }
        ],
        chartData: [
          { name: "Housing", value: housing },
          { name: "Utilities", value: utilities },
          { name: "Groceries", value: groceries },
          { name: "Transportation", value: trans },
          { name: "Personal", value: personal }
        ]
      };
    },
    guide: {
      overview: "Track and organize your monthly living expenses to understand where your money is going.",
      howToUse: "Enter your monthly expenses for housing, utilities, groceries, transportation, and personal shopping.",
      formulaExplanation: "Sums all your inputs to find your total monthly spending and calculates each category's percentage of your overall budget.",
      faq: [
        { question: "Why should I track my expenses?", answer: "Tracking your expenses reveals leaks in your budget and helps you identify opportunities to save or redirect cash to your financial goals." },
        { question: "How often should I review my expenses?", answer: "We suggest tracking your spending weekly and reviewing your total expenses at the end of each month to stay on track." }
      ],
      seoContent800Words: `Many households struggle to build wealth because they fail to track their daily, out-of-pocket expenses, letting small leaks drain their budgets. Without a clear view of your monthly spending, it is easy to overspend on non-essentials and struggle to save. To secure your financial health, you must list and analyze your living expenses regularly.

### The Financial Benefits of Expense Tracking

Reviewing your monthly expenses allows you to identify exactly where your hard-earned money is going, helping you make smart, intentional changes. This tool separates your expenses into key categories—housing, utilities, groceries, transportation, and personal shopping—letting you see which areas take up the largest portion of your income.

* **Benchmark Your Housing**: Your housing rent or mortgage payment should ideally be kept under 30.0% of your gross monthly income to avoid being "house poor."
* **Audit Your Groceries**: Groceries are a flexible expense that can easily scale out of control if you frequently buy convenience meals or fail to plan.

This tool calculates these parameters clearly so you can identify spending leaks and optimize your cash flows.`
    }
  },
  {
    id: "household-budget-calculator",
    name: "Household Budget Calculator",
    description: "Manage your overall household income and joint expenses.",
    category: "budget",
    inputs: [
      { id: "incomeA", label: "Partner A Monthly Net Income ($)", type: "currency", defaultValue: 4500, min: 0, max: 50000, step: 100 },
      { id: "incomeB", label: "Partner B Monthly Net Income ($)", type: "currency", defaultValue: 3800, min: 0, max: 50000, step: 100 },
      { id: "jointExpenses", label: "Combined Joint Monthly Expenses ($)", type: "currency", defaultValue: 5200, min: 0, max: 20000, step: 100 }
    ],
    calculate: (inputs) => {
      const incA = inputs.incomeA || 0;
      const incB = inputs.incomeB || 0;
      const exp = inputs.jointExpenses || 0;

      const totalIncome = incA + incB;
      const netSavings = Math.max(0, totalIncome - exp);
      const savingsRate = totalIncome > 0 ? (netSavings / totalIncome) * 100 : 0;

      return {
        outputs: [
          { label: "Total Combined Household Income", value: totalIncome, type: "currency", highlighted: true },
          { label: "Household Net Monthly Savings", value: netSavings, type: "currency", highlighted: true },
          { label: "Combined Household Savings Rate (%)", value: savingsRate, type: "percentage" }
        ],
        chartData: [
          { name: "Joint Expenses", value: exp },
          { name: "Net Savings", value: netSavings }
        ]
      };
    },
    guide: {
      overview: "Manage your overall household income and joint expenses as a couple to achieve your shared financial goals.",
      howToUse: "Enter the monthly net incomes for both partners, along with your combined joint monthly expenses.",
      formulaExplanation: "Sums both partner incomes, subtracts joint expenses, and calculates your shared household savings rate.",
      faq: [
        { question: "How can couples manage joint expenses?", answer: "Many couples utilize joint checkings accounts to pay shared bills like rent or utilities, while keeping individual accounts to handle personal wants." },
        { question: "What is a healthy household savings rate?", answer: "We suggest keeping your shared household savings rate at 15% to 20% of your combined net income to secure your shared retirement goals on time." }
      ],
      seoContent800Words: `Managing finances as a couple requires transparent communication, shared goals, and a unified plan. Without coordinating your personal incomes and joint bills, it is easy to find yourself experiencing misunderstandings or struggling to build a shared nest egg. To secure your family's future, you must list and analyze your joint cash flows.

### The Mathematics of Household Cash Flows

Your overall family savings are driven by your combined couples incomes and joint monthly expenses:
$$\\text{Total Income} = \\text{Partner A Income} + \\text{Partner B Income}$$
$$\\text{Net Savings} = \\text{Total Income} - \\text{Joint Monthly Expenses}$$
$$\\text{Savings Rate} = \\frac{\\text{Net Savings}}{\\text{Total Income}} \\times 100$$

For example, if Partner A earns $4,500 and Partner B earns $3,800 net:
* Your combined household monthly income is **$8,300**.
* If your joint expenses (including mortgage, groceries, car payments, and bills) equal $5,200:
* Your shared net savings is:
  $$\\text{Savings} = \\$8,300 - \\$5,200 = \\$3,100$$
* Your combined family savings rate is a highly optimized **37.35%**, helping you compound joint wealth quickly.

This tool calculates these parameters clearly, helping couples coordinate their cash flows and track their shared financial goals.`
    }
  },
  {
    id: "emergency-fund-calculator",
    name: "Emergency Fund Calculator",
    description: "Determine your ideal emergency savings buffer using monthly essential spending.",
    category: "budget",
    isHighCPC: true,
    inputs: [
      { id: "expenses", label: "Monthly Essential Spending ($)", type: "currency", defaultValue: 3200, min: 500, max: 20000, step: 100 },
      { id: "months", label: "Ideal Safety Buffer (Months)", type: "number", defaultValue: 6, min: 3, max: 12, step: 1 }
    ],
    calculate: (inputs) => {
      const raw = inputs.expenses || 3200;
      const buffer = inputs.months || 6;

      const target = raw * buffer;

      return {
        outputs: [
          { label: "Required Emergency Savings Target", value: target, type: "currency", highlighted: true },
          { label: "Safety Buffer Duration (Months)", value: buffer, type: "number" },
          { label: "Assumed Essential Monthly Spending", value: raw, type: "currency" }
        ],
        chartData: [
          { name: "Monthly Cost", value: raw },
          { name: "Total Target Fund Needed", value: target }
        ]
      };
    },
    guide: {
      overview: "Determine your ideal emergency savings buffer using monthly essential spending.",
      howToUse: "Enter your monthly essential spending and select your desired safety buffer in months.",
      formulaExplanation: "Multiplies your monthly essential spending by your safety buffer to find your target fund size.",
      faq: [
        { question: "What is an emergency fund?", answer: "A dedicated savings buffer used to handle unexpected expenses—like car repairs, medical emergencies, or job loss—stress-free." },
        { question: "How many months of savings should I have?", answer: "Most planners suggest setting aside 3 to 6 months of living expenses for single-income households, and up to 9 to 12 months for freelancers or families with variable income." }
      ],
      seoContent800Words: `An **Emergency Fund** is the foundation of your personal financial security. Life is full of unpredictable events—such as sudden job losses, medical emergencies, car repairs, or home leaks—that can derail your budget if you are unprepared. Without a comfortable cash buffer, you may find yourself forced to rely on high-interest credit cards or personal loans, which can set your wealth goals back by years. To protect yourself, you must build a robust emergency fund.

### The Mathematics of Safety Buffers

To find your target emergency fund size, you must calculate your monthly essential spending—the minimum cash required to discover your needs (including rent, groceries, utilities, and debt payments)—and select your ideal safety buffer in months:
$$\\text{Required emergency Fund} = \\text{Monthly Essential Spending} \\times \\text{Safety Buffer (in Months)}$$

For example, if your monthly essential utilities and rent require $3,200:
* Under a **3-Month Buffer**: Your required emergency cushion is **$9,600**. This is a great starting target for households with dual, stable incomes.
* Under a **6-Month Buffer**: Your required emergency savings target rises to **$19,200**. This is highly recommended for single-income households or families with toddlers.
* Under a **12-Month Buffer**: Your safety target rises to **$38,400**. This is essential for freelancers, commission-based sales professionals, or families with highly variable incomes.

This tool calculates these targets clearly, helping you set your savings milestones.

### Strategies to Build and Protect Your Emergency Fund

* **Store Cash in a high-Yield Savings Account (HYSA)**: Keep your emergency savings in a separate, high-yield digital bank account offering interest rates exceeding 4.0% to protect your purchasing power from being eroded by inflation.
* **Keep Funds Completely Separate**: Bypassing your daily debit cards and checkings accounts is critical to keeping your savings safe.
* **Refill Your Cushion Immediately**: If you draw down your emergency fund to handle an unexpected event, adjust your budget to refill the cushion as quickly as possible.`
    }
  },
  {
    id: "vacation-budget-calculator",
    name: "Vacation Budget Planner",
    description: "Budget for flight tickets, hotels, food, and sightseeing on your next trip.",
    category: "budget",
    inputs: [
      { id: "flights", label: "Flight Tickets & Transport Cost ($)", type: "currency", defaultValue: 1200, min: 0, max: 20000, step: 100 },
      { id: "hotel", label: "Hotel & Lodging Costs ($)", type: "currency", defaultValue: 1500, min: 0, max: 50000, step: 100 },
      { id: "food", label: "Estimated Dining & Food Cost ($)", type: "currency", defaultValue: 800, min: 0, max: 10000, step: 50 },
      { id: "activities", label: "Sightseeing & Activities ($)", type: "currency", defaultValue: 600, min: 0, max: 10000, step: 50 }
    ],
    calculate: (inputs) => {
      const flights = inputs.flights || 0;
      const hotel = inputs.hotel || 0;
      const food = inputs.food || 0;
      const act = inputs.activities || 0;

      const total = flights + hotel + food + act;

      return {
        outputs: [
          { label: "Total Estimated Vacation Budget", value: total, type: "currency", highlighted: true },
          { label: "Lodging Percentage (%)", value: total > 0 ? (hotel / total) * 100 : 0, type: "percentage" },
          { label: "Transport Percentage (%)", value: total > 0 ? (flights / total) * 100 : 0, type: "percentage" }
        ],
        chartData: [
          { name: "Flights", value: flights },
          { name: "Hotel", value: hotel },
          { name: "Food", value: food },
          { name: "Activities", value: act }
        ]
      };
    },
    guide: {
      overview: "Plan and budget for your next vacation, factoring in flights, lodging, food, and activities to enjoy your trip stress-free.",
      howToUse: "Enter your estimated expenses for flight tickets, hotels, dining, and sightseeing activities.",
      formulaExplanation: "Sums all your inputs to find your total vacation cost and calculates each category's percentage of your overall budget.",
      faq: [
        { question: "How can I lower my vacation costs?", answer: "Book your flights and lodging well in advance, travel during the off-season, and set a hard daily limit on dining and shopping." },
        { question: "Should I use credit card rewards to fund my trip?", answer: "Using travel credit card reward points or miles is an excellent, tax-free way to cover flight and hotel costs without dipping into your cash savings." }
      ],
      seoContent800Words: `Traveling and exploring the world is one of life's greatest pleasures, but taking a vacation can quickly become a significant financial stressor if you fail to budget. Without a clear view of your flight, lodging, and dining costs beforehand, it is easy to overspend on non-essentials and return home facing credit card debt. To enjoy your trip completely relaxed, you must draft a detailed vacation budget.

### The Mathematics of Travel Budgeting

Your overall vacation cost is driven by major fixed expenses (like flights and hotel lodging) and variable daily expenses (like dining, shopping, and sightseeing):
$$\\text{Total Vacation Budget} = \\text{Flights} + \\text{Lodging} + \\text{Dining} + \\text{Activities}$$

For example, planning a week-long getaway:
* Variable transport costs (flights & transit): **$1,200**
* Variable accommodation (hotel rent/lodging): **$1,500**
* Estimated daily food costs: **$800**
* Sightseeing & museum activities: **$600**
* Your total estimated vacation budget is **$4,100**.

This tool calculates these parameters clearly, helping you set savings targets before your departure date.

### Strategic Guidelines for Budget Travelers

1. **Leverage Travel Rewards**: Use airline miles or credit card point promotions to cover expensive flights or hotel bookings, saving you thousands of dollars in cash outlays.
2. **Set a Hard Daily Limit**: Establish a realistic, daily spending limit for dining, drinks, and souvenirs, and monitor your outlays daily to avoid exceeding your overall budget.
3. **Set Up a Travel Savings Bucket**: Move small, recurring amounts from your weekly paychecks into a dedicated savings bucket throughout the year to fund your vacation stress-free.`
    }
  },
  {
    id: "grocery-budget-calculator",
    name: "Grocery Budget Planner",
    description: "Plan your weekly and monthly grocery spending based on family size.",
    category: "budget",
    inputs: [
      { id: "familySize", label: "Household Family Size", type: "number", defaultValue: 3, min: 1, max: 10, step: 1 },
      { id: "budgetTier", label: "Grocery Spending Tier", type: "select", defaultValue: 2, options: [
        { label: "Thrifty ($50/person/week)", value: 50 },
        { label: "Moderate ($75/person/week)", value: 75 },
        { label: "Liberal ($110/person/week)", value: 110 }
      ]}
    ],
    calculate: (inputs) => {
      const size = inputs.familySize || 3;
      const tier = inputs.budgetTier || 75;

      const weeklyTarget = size * tier;
      const monthlyTarget = weeklyTarget * 4.33; // average weeks in a month

      return {
        outputs: [
          { label: "Estimated Weekly Grocery Target", value: weeklyTarget, type: "currency", highlighted: true },
          { label: "Estimated Monthly Grocery Target", value: monthlyTarget, type: "currency", highlighted: true },
          { label: "Annual Combined Grocery Spending", value: monthlyTarget * 12, type: "currency" }
        ],
        chartData: [
          { name: "Weekly Cost", value: weeklyTarget },
          { name: "Monthly Cost", value: monthlyTarget }
        ]
      };
    },
    guide: {
      overview: "Plan your weekly and monthly grocery spending based on your household family size and preferred spending tier.",
      howToUse: "Enter your family size and select your grocery spending tier (Thrifty, Moderate, or Liberal).",
      formulaExplanation: "Calculates weekly spending by multiplying your family size by your tier's dollar value, and scales it to find your monthly target.",
      faq: [
        { question: "How can I lower my grocery costs?", answer: "Plan your meals, shop with a list, buy generic store brands, and cook at home to significantly lower your weekly costs." },
        { question: "Does meal prepping save money?", answer: "Yes, meal prepping helps reduce food waste, prevents impulse takeout orders during busy weeks, and lowers your overall grocery spending." }
      ],
      seoContent800Words: `Groceries represent one of the largest and most flexible components of your monthly household budget. Unlike housing rent or auto loans which are fixed, what you spend at the supermarket fluctuates based on your household size, meal planning habits, and food priorities. Without a clear monthly plan, it is easy to overspend at the grocery store, letting food waste drain your budget. To manage your family's finances, you must draft a detailed grocery budget.

### The Mathematics of supermarket spending

Your overall grocery target is driven by your family size and preferred spending tier (Thrifty, Moderate, or Liberal):
$$\\text{Weekly Target} = \\text{Family Size} \\times \\text{Tier Value}$$
$$\\text{Monthly Target} = \\text{Weekly Target} \\times 4.33$$

For a family of 3 under a **Moderate** tier ($75/person/week):
* Your weekly grocery target is:
  $$\\text{Weekly} = 3 \\times \\$75 = \\$225$$
* This translates to a monthly grocery target of **$974.25**.

This tool calculates these targets clearly so you can shop with confidence and stay on track.

### Practical Tips to Eliminate Food Waste and Lower grocery Costs

1. **Draft a Weekly Meal Plan**: Review your pantry and plan your weekly meals before visiting the supermarket, buying only the ingredients you need to completely eliminate waste.
2. **Focus on Store Generic Brands**: Opt for generic store brands over commercial, heavily marketed names to instantly save 20% to 30% on everyday products.
3. **Commit to Grocery Lists**: Never shop on an empty stomach, and stick strictly to your listed items to bypass high-cost impulse items.`
    }
  },
  {
    id: "rent-affordability-calculator",
    name: "Rent Affordability Estimator",
    description: "Determine your maximum safe monthly rent based on gross salary.",
    category: "budget",
    inputs: [
      { id: "salary", label: "Annual Gross Salary ($)", type: "currency", defaultValue: 65000, min: 10000, max: 500000, step: 2000 },
      { id: "standard", label: "Rental Standard Model", type: "select", defaultValue: 30, options: [
        { label: "30% Allocation Rule", value: 30 },
        { label: "40x Rent Income Standard", value: 40 }
      ]}
    ],
    calculate: (inputs) => {
      const salary = inputs.salary || 0;
      const model = inputs.standard || 30;

      let maxRent = 0;
      if (model === 30) {
        // 30% of monthly gross income
        maxRent = (salary / 12) * 0.30;
      } else {
        // Rent = Salary / 40
        maxRent = salary / 40;
      }

      return {
        outputs: [
          { label: "Maximum Safe Monthly Rent Budget", value: maxRent, type: "currency", highlighted: true },
          { label: "Monthly Gross Earnings", value: salary / 12, type: "currency" },
          { label: "Assumed Allocation Ratio Used (%)", value: model === 30 ? 30 : 25, type: "percentage" }
        ],
        chartData: [
          { name: "Rents Limit", value: maxRent },
          { name: "Tax & Safe Discretionary Cash", value: (salary / 12) - maxRent }
        ]
      };
    },
    guide: {
      overview: "Determine your maximum safe monthly Rent based on your gross annual salary, using standard real estate renting metrics.",
      howToUse: "Enter your gross annual salary and select your rental standard model (30% rule or 40x income standards).",
      formulaExplanation: "Calculates affordable monthly rent by applying standard ratios to your gross salary, helping you secure safe housing.",
      faq: [
        { question: "What is the 30% rent rule?", answer: "A personal finance standard that advises keeping your monthly rent or housing costs under 30.0% of your gross monthly income." },
        { question: "What is the 40x rent rule?", answer: "A common requirement used by landlords in high-cost cities, stating that a renter's gross annual income must equal at least 40 times their monthly rent." }
      ],
      seoContent800Words: `Securing comfortable, safe housing is typically our largest monthly expense. However, in high-cost urban markets, renting can quickly swallow up a massive portion of your paycheck if you fail to set local limits. Stretch your budget too thin on rent, and you may find yourself "house poor"—struggling to save, pay off debt, or handle emergency expenses. To shop for apartments confidently, you must determine your safe rental budget.

### Analyzing Common Housing Budget Standards

To find your safe rental budget, real estate professionals and financial advisors use two primary standards:
1. **The 30% Rule**: Keep your overall monthly housing rent under 30.0% of your gross monthly income (before taxes):
   $$\\text{Max Rent} = \\frac{\\text{Annual Salary}}{12} \\times 0.30$$
2. **The 40x Income Standard**: A requirement used by landlords in competitive urban markets (like New York or San Francisco), stating that your gross annual salary must equal at least 40 times your monthly rent:
   $$\\text{Max Rent} = \\frac{\\text{Annual Salary}}{40}$$
   *(Note: This equates to allocating exactly 30% of your gross income to rent.)*

On an annual salary of $65,000 ($5,416.67 a month):
* Under the 30% rule: Your maximum safe monthly rent budget is **$1,625.00**.
* Under the 40x rule: Your maximum safe monthly rent budget is **$1,625.00**.

This tool calculates these parameters clearly, helping you set a realistic budget for apartment hunting.

### Smart Strategies for Renters in High-Cost Markets

* **Audit Utility Costs**: Remember that your apartment rent does not cover utility bills—such as electricity, gas, internet, and trash pickup. Ensure you set aside an extra $150 to $200 in your monthly budget to cover these costs.
* **Consider Roommates**: Sharing a multi-bedroom apartment with roommates splits your rent and utility bills, instantly freeing up hundreds of dollars in your monthly budget.
* **Build Great Credit Ratings**: Landlords check your credit history before approving renting applications. Maintaining a high credit rating helps you secure apartments over other qualified renters.`
    }
  },
  {
    id: "cost-of-living",
    name: "Cost of Living Estimator",
    description: "Compare cost of living variations between regions based on essential price shifts.",
    category: "budget",
    inputs: [
      { id: "currentNetIncome", label: "Current Net Income ($)", type: "currency", defaultValue: 5000, min: 500, max: 50000, step: 500 },
      { id: "livingIndexChange", label: "Destination Cost Index Shift (%)", type: "percentage", defaultValue: 12, min: -50, max: 100, step: 1 }
    ],
    calculate: (inputs) => {
      const current = inputs.currentNetIncome || 5000;
      const indexShift = inputs.livingIndexChange || 12;

      const multiplier = 1 + (indexShift / 100);
      const requiredIncome = current * multiplier;
      const deficit = requiredIncome - current;

      return {
        outputs: [
          { label: "Equivalent Target Net Income Needed", value: requiredIncome, type: "currency", highlighted: true },
          { label: "Required Change in Take-Home Pay", value: deficit, type: "currency", highlighted: true },
          { label: "Cost of Living Shift Ratio (%)", value: indexShift, type: "percentage" }
        ],
        chartData: [
          { name: "Current Income", value: current },
          { name: "Shift in Cost", value: Math.abs(deficit) }
        ]
      };
    },
    guide: {
      overview: "Compare cost of living variations between regions to understand how much you need to earn to maintain your standard of living after relocating.",
      howToUse: "Enter your current net income and the estimated cost index shift for your destination city.",
      formulaExplanation: "Calculates your equivalent target income by applying the destination cost shift to your current earnings.",
      faq: [
        { question: "What is cost of living?", answer: "The amount of money required to cover basic living expenses—like housing, food, taxes, and healthcare—in a specific region." },
        { question: "How does inflation impact regional cost of living?", answer: "Inflation rates vary regionally, causing essential expenditures in metropolitan areas to rise faster than rural areas." }
      ],
      seoContent800Words: `Relocating to a new city presents a wealth of career opportunities, but it also introduces significant cost-of-living variations that can derail your budget if you are unprepared. A salary that supports an affluent lifestyle in a mid-sized town can feel tight in a major metropolitan area. To make a smart relocation decision, you must evaluate how cost index shifts affect your purchasing power.

### Understanding Cost of Living Index Shifts

Cost of Living indexes analyze the pricing of essential consumer goods and services—such as housing rents, groceries, taxes, gas, and utilities—in different regions compared to a national average benchmark.
The formula to calculate your equivalent target net income is:
$$\\text{Equivalent Income} = \\text{Current Income} \\times \\left(1 + \\frac{\\text{Index Shift}}{100}\\right)$$

For example, on a current net monthly income of $5,000, relocating to a city with a **12% higher cost index**:
* Your equivalent target net income is:
  $$\\text{Equivalent} = \\$5,000 \\times 1.12 = \\$5,600$$
* You must earn an extra **$600 a month** in take-home pay to maintain your standard of living.

This tool calculates these gaps clearly, helping you negotiate salaries for relocation.

### Strategies to Manage High Cost of Living Regions

1. **Focus on Net Purchasing Power**: Never evaluate career relocations using nominal salary increases alone. Make sure the prospective pay rise comfortably exceeds local cost index shifts.
2. **Audit housing Rents First**: Housing is the primary driver of cost-of-living variations. Research apartment prices in your target neighborhoods before committing to job offers.
3. **Draft a Relocation Budget**: Plan for upfront relocation costs—such as moving trucks, lease deposits, and transport expenses—to ensure a smooth, worry-free transition.`
    }
  },
  {
    id: "income-vs-expense",
    name: "Income vs. Expense Balance",
    description: "Compare your total monthly income against total living expenses.",
    category: "budget",
    inputs: [
      { id: "income", label: "Monthly Take-Home Income ($)", type: "currency", defaultValue: 6000, min: 100, max: 100000, step: 100 },
      { id: "expenses", label: "Total Monthly Living Expenses ($)", type: "currency", defaultValue: 4800, min: 100, max: 100000, step: 100 }
    ],
    calculate: (inputs) => {
      const inc = inputs.income || 0;
      const exp = inputs.expenses || 0;

      const netSurplus = inc - exp;
      const ratio = inc > 0 ? (exp / inc) * 100 : 0;

      return {
        outputs: [
          { label: "Net Monthly Savings Surplus", value: netSurplus, type: "currency", highlighted: true },
          { label: "Expense-to-Income Ratio (%)", value: ratio, type: "percentage", highlighted: true },
          { label: "Monthly Income Retained (%)", value: inc > 0 ? (netSurplus / inc) * 100 : 0, type: "percentage" }
        ],
        chartData: [
          { name: "Monthly Income", value: inc },
          { name: "Living Expenses", value: exp }
        ]
      };
    },
    guide: {
      overview: "Compare your total monthly income against total living expenses to identify your savings potential.",
      howToUse: "Enter your monthly take-home income and your total monthly living expenses.",
      formulaExplanation: "Subtracts your expenses from your income to find your savings surplus and calculates your overall expense-to-income ratio.",
      faq: [
        { question: "What is an expense-to-income ratio?", answer: "The percentage of your income that goes toward paying your monthly expenses. Keeping this ratio below 80% ensures you have room to save." },
        { question: "How can I increase my monthly surplus?", answer: "Focus on increasing your income through side projects, or cut back on non-essential lifestyle expenses to lower your costs." }
      ],
      seoContent800Words: `At the heart of all investment decisions is a simple, fundamental personal finance equation: your **Income vs. Expense Balance**. If your monthly expenses consistently equal or exceed your take-home pay, you are living "paycheck to paycheck"—a stressful cycle that leaves you vulnerable to sudden emergencies and blocks you from building wealth. Securing your peace of mind requires optimizing your savings surplus.

### The Mathematics of Budget Surpluses

Your monthly savings surplus is the difference between your net take-home income and your total expenses:
$$\\text{Net Surplus} = \\text{Monthly Income} - \\text{Monthly Expenses}$$
Your **Expense-to-Income Ratio** represents the percentage of your paycheck consumed by living costs:
$$\\text{Ratio} = \\frac{\\text{Monthly Expenses}}{\\text{Monthly Income}} \\times 100$$

On a net take-home monthly income of $6,000, with total expenses of $4,800:
* Your monthly savings surplus is **$1,200**.
* Your expense-to-income ratio is **80.0%**, meaning you hold onto exactly **20.0%** of your paycheck.

This tool calculates these key parameters clearly, helping you set your savings targets.

### Strategies to Boost Your budget surplus

* **Audit and Trim Re-occurring Bills**: Regularly review your bank statements and prune non-essential subscriptions, premium cable tiers, and unused memberships to lower your basic living costs.
* **Adopt the 24-Hour Purchase Delay Rule**: Avoid impulsive spending on high-ticket luxury wants by waiting a full 24 hours before completing purchase decisions.
* **Allocate Pay Raises Straight to Savings**: Direct any pay rises, bonuses, or side income straight to your high-yield savings or brokerage accounts to bypass lifestyle inflation.`
    }
  },
  {
    id: "financial-health",
    name: "Financial Health Score Card",
    description: "Determine your subjective financial health rating by scoring key household habits.",
    category: "budget",
    inputs: [
      { id: "savingsGrade", label: "Do you save 15%+ of your income?", type: "select", defaultValue: 10, options: [
        { label: "Yes, consistently (10 pts)", value: 10 },
        { label: "Sometimes (5 pts)", value: 5 },
        { label: "No, rarely (0 pts)", value: 0 }
      ]},
      { id: "emergencyGrade", label: "Do you hold a 3-month emergency fund?", type: "select", defaultValue: 10, options: [
        { label: "Yes, fully funded (10 pts)", value: 10 },
        { label: "Partially funded (5 pts)", value: 5 },
        { label: "No, empty (0 pts)", value: 0 }
      ]},
      { id: "debtGrade", label: "Are your debts paid off/managed well?", type: "select", defaultValue: 10, options: [
        { label: "Yes, debt-free or low interest (10 pts)", value: 10 },
        { label: "Have some high-interest debt (5 pts)", value: 5 },
        { label: "Struggling with heavy debt (0 pts)", value: 0 }
      ]}
    ],
    calculate: (inputs) => {
      const s = inputs.savingsGrade || 10;
      const e = inputs.emergencyGrade || 10;
      const d = inputs.debtGrade || 10;

      const totalScore = s + e + d;
      let rating = "Fair";
      if (totalScore >= 25) rating = "Excellent (Strong Resilience)";
      else if (totalScore >= 15) rating = "Good (Moderate Stability)";
      else rating = "Needs Attention (High Vulnerability)";

      return {
        outputs: [
          { label: "Your Overall Financial Health Score (out of 30)", value: totalScore, type: "number", highlighted: true },
          { label: "Subjective Financial Health Rating", value: rating, type: "text", highlighted: true }
        ],
        chartData: [
          { name: "My Score", value: totalScore },
          { name: "Remaining Gap to Max Fit", value: 30 - totalScore }
        ]
      };
    },
    guide: {
      overview: "Determine your subjective financial health scorecard based on key personal finance habits and markers of household resilience.",
      howToUse: "Score your habits honestly across savings consistency, emergency fund coverage, and debt management levels.",
      formulaExplanation: "Sums your graded habit points to find your final resilience score on a scale of 0 to 30, categorizing your financial health.",
      faq: [
        { question: "What is a Financial Health Score?", answer: "A quick diagnostic checklist used to evaluate your financial household safety, resilience, and wealth-building habits." },
        { question: "How can I improve my financial health score?", answer: "Focus on automating your savings transfers, building up your emergency fund buffer, and prepaying high-interest credit card debt." }
      ],
      seoContent800Words: `Securing your household finances requires looking beyond a single metric like your salary. True financial resilience is achieved when your financial habits work together—combining consistent savings, fully funded emergency cushions, and structured debt management to shield your household from unexpected events. To evaluate your resilience, you must review your overall financial scorecard.

### The Graded Habits of Financial Resilience

This scorecard evaluates your household stability across three primary pillars:
1. **Savings Discipline (10 Points)**: Do you consistently save and invest at least 15% of your take-home pay? Regular savers build wealth quickly and bypass paycheck-to-paycheck cycles.
2. **Emergency Readiness (10 Points)**: Do you hold a fully funded emergency cash cushion covering at least 3 months of essential home expenses? Safety cash protects your budget from sudden shocks.
3. **Debt Management (10 Points)**: Are you debt-free or managing your debts responsibly? Keeping credit card balances or high-interest personal loans low frees up cash to grow your wealth.

Adding up your scores reveals your overall rating:
* **25 to 30 Points (Excellent)**: Strong financial resilience. You are well-prepared for sudden job changes or emergencies, and have a clear wealth-building plan.
* **15 to 20 Points (Good)**: Moderate stability. You are doing well, but have some vulnerabilities—such as a small cash cushion or some card debt—that need attention.
* **0 to 10 Points (Needs Attention)**: High vulnerability. You are highly vulnerable to sudden expenses. Focus immediately on building your cash cushion and paying off debt.

This rating tells you exactly where to focus your financial energy to stay on track.`
    }
  }
];
